/**
 * Zod mirrors of the vocabulary, for the routes that carry it.
 *
 * These live here rather than in api-contract so that the types and the wire
 * schemas cannot drift: `ReadingSchema` is a discriminated union on the same
 * `evidence` tag the TypeScript union uses, so a branch added to one without
 * the other fails to compile at the `satisfies` checks at the bottom.
 *
 * A note on the field-drop detector (engineering rule 1): `ControlApiClient`
 * throws under vitest when a route returns a field the contract does not
 * declare. `EvidenceBagSchema` is a `z.record`, which keeps everything, so an
 * evidence bag can grow a key without tripping it. Everything else here is
 * exact on purpose - the detector existing is why `Sale.sellerCommission` was
 * found to have been invisible since migration 0018.
 */

import { z } from 'zod';
import { EVIDENCE, HEALTH, type Health, type Reading } from './health.js';
import { SEVERITY, type Finding, type Scope } from './finding.js';
import { REMEDIATION_LEVELS, RISK, type RunbookPreview } from './runbook.js';
import type { InvariantOutcome, InvariantResult } from './invariant.js';
import type { ProbeDetail, ProbeResult } from './probe.js';

export const HealthSchema = z.enum(HEALTH);
export const EvidenceSchema = z.enum(EVIDENCE);
export const SeveritySchema = z.enum(SEVERITY);
export const RemediationLevelSchema = z.enum(REMEDIATION_LEVELS);
export const RunbookLevelSchema = z.enum(['safe', 'state', 'dangerous']);
export const RiskSchema = z.enum(RISK);

export const EvidenceValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const EvidenceBagSchema = z.record(EvidenceValueSchema);

export const ScopeSchema = z.object({
  tenantSlug: z.string().optional(),
  saleId: z.string().optional(),
  ringId: z.string().optional(),
  lotId: z.string().optional(),
});

export const readingSchema = <T extends z.ZodTypeAny>(value: T) =>
  z.discriminatedUnion('evidence', [
    z.object({ evidence: z.literal('observed'), at: z.number(), value, health: HealthSchema }),
    z.object({
      evidence: z.literal('stale'), at: z.number(), value,
      health: z.enum(['degraded', 'failed', 'unknown']),
      ageMs: z.number(), budgetMs: z.number(),
    }),
    z.object({ evidence: z.literal('absent'), health: z.literal('unknown'), why: z.string() }),
  ]);

export const ProbeDetailSchema = EvidenceBagSchema;
export const ProbeReadingSchema = readingSchema(ProbeDetailSchema);

export const ProbeResultSchema = z.object({
  id: z.string(),
  title: z.string(),
  meaning: z.string(),
  reading: ProbeReadingSchema,
  tookMs: z.number(),
});

export const FindingSchema = z.object({
  code: z.string(),
  severity: SeveritySchema,
  message: z.string(),
  scope: ScopeSchema,
  evidence: EvidenceBagSchema,
  runbook: z.string().optional(),
  fix: z.object({ action: z.string(), args: z.record(z.unknown()) }).optional(),
});

export const InvariantOutcomeSchema = z.enum(['HELD', 'FAILED', 'NOT_APPLICABLE', 'NOT_CHECKED']);

export const InvariantResultSchema = z.object({
  code: z.string(),
  title: z.string(),
  outcome: InvariantOutcomeSchema,
  findings: z.array(FindingSchema),
  why: z.string().optional(),
});

export const FindingSummarySchema = z.object({
  errors: z.number().int(),
  warnings: z.number().int(),
  info: z.number().int(),
});

export const RunbookPreviewSchema = z.object({
  runbook: z.string(),
  level: RunbookLevelSchema,
  risk: RiskSchema,
  scope: ScopeSchema,
  reason: z.string(),
  willChange: z.array(z.string()),
  willNotChange: z.array(z.string()),
  expected: EvidenceBagSchema,
  blockedBy: z.array(z.string()),
});

// ---------------------------------------------------------------------------
// Parity: the schemas and the types describe the same thing, or this file fails
// to compile. Cheaper than a test, and it cannot be forgotten.
// ---------------------------------------------------------------------------

type SchemaOf<T extends z.ZodTypeAny> = z.infer<T>;

const _health: Health = 'healthy' satisfies SchemaOf<typeof HealthSchema>;
const _scope: Scope = {} satisfies SchemaOf<typeof ScopeSchema>;
const _outcome: InvariantOutcome = 'HELD' satisfies SchemaOf<typeof InvariantOutcomeSchema>;
void _health; void _scope; void _outcome;

/** Structural assertions: the inferred wire shape is assignable to the type. */
export type FindingWire = SchemaOf<typeof FindingSchema>;
export type ProbeResultWire = SchemaOf<typeof ProbeResultSchema>;
export type InvariantResultWire = SchemaOf<typeof InvariantResultSchema>;
export type RunbookPreviewWire = SchemaOf<typeof RunbookPreviewSchema>;
export type ProbeReadingWire = SchemaOf<typeof ProbeReadingSchema>;

const _f = (x: FindingWire): Finding => x;
const _p = (x: ProbeResultWire): ProbeResult => x;
const _i = (x: InvariantResultWire): InvariantResult => x;
const _r = (x: RunbookPreviewWire): RunbookPreview => x;
const _rd = (x: ProbeReadingWire): Reading<ProbeDetail> => x;
void _f; void _p; void _i; void _r; void _rd;
